import { FC, useState } from 'react';
import { WidgetCell, WidgetProps } from './WidgetCell';
import { Box, Slider, Stack, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { graphql } from '../../../../gql';
import { useApiMutation } from '../../../../query/hooks';
import { useIntl } from 'react-intl';
import { SatisfactionRateIcon } from '../../../../journey/utils/SatisfactionRateIcon';
import { determineColor } from '../../../../utils/getOodColor';
import { getNightOrDayPalette } from '../../../../utils/getNightOrDayPalette';
import { mutationResponseHandler } from '../../../../utils/mutationResponseHandler';
import slugify from 'slugify';
import { widgetDefinitions } from './widgetDefinitions';
import { useSnackbar } from '../../../../store/snackbar';
import { useDebounceEffect } from '../../../../utils/useDebounceEffect';

const SatisfactionSlider = styled(Slider)`
	.MuiSlider-rail {
		width: 1px;
		opacity: 1 !important;
		background-image: linear-gradient(
			to top,
			${({ theme }) => determineColor(theme, 0)} 0,
			${({ theme }) => determineColor(theme, 20)} 20%,
			${({ theme }) => determineColor(theme, 40)} 40%,
			${({ theme }) => determineColor(theme, 60)} 60%,
			${({ theme }) => determineColor(theme, 80)} 80%
		);
	}

	.MuiSlider-thumb.MuiSlider-thumbSizeMedium.MuiSlider-thumbColorPrimary.MuiSlider-thumb.MuiSlider-thumbSizeMedium.MuiSlider-thumbColorPrimary {
		width: 12px;
		height: 12px;
		border: 2px solid ${({ theme }) => theme.palette[getNightOrDayPalette(theme)][0]} !important;
		background: transparent;
		margin-bottom: 3px;
		box-shadow:
			0px 3px 1px -2px rgba(0, 0, 0, 0.2),
			0px 2px 2px 0px rgba(0, 0, 0, 0.14),
			0px 1px 5px 0px rgba(0, 0, 0, 0.12);

		&:hover {
			box-shadow:
				0px 3px 1px -2px rgba(0, 0, 0, 0.2),
				0px 2px 2px 0px rgba(0, 0, 0, 0.14),
				0px 1px 5px 0px rgba(0, 0, 0, 0.12) !important;
		}

		&::before {
			box-shadow: none;
		}
	}

	.MuiSlider-mark {
		border: none;
		width: 8px;
		height: 8px;
		border-radius: 4px;
		outline: 1px solid ${({ theme }) => theme.palette[getNightOrDayPalette(theme)][0]};

		&[data-index='0'] {
			background-color: ${({ theme }) => determineColor(theme, 0)};
		}
		&[data-index='1'] {
			background-color: ${({ theme }) => determineColor(theme, 20)};
		}
		&[data-index='2'] {
			background-color: ${({ theme }) => determineColor(theme, 40)};
		}
		&[data-index='3'] {
			background-color: ${({ theme }) => determineColor(theme, 60)};
		}
		&[data-index='4'] {
			background-color: ${({ theme }) => determineColor(theme, 80)};
		}
	}
`;

const updateSatisfactionValue = graphql(`
	mutation satisfactionCell_updateSatisfaction($activityId: UUID!, $satisfactionValue: Int!) {
		updateActivity(by: { id: $activityId }, data: { satisfaction: $satisfactionValue }) {
			ok
			errorMessage
		}
	}
`);

const STROKE_WIDTH = 2;

const MARKS = [
	{
		value: 0,
	},
	{
		value: 25,
	},
	{
		value: 50,
	},
	{
		value: 75,
	},
	{
		value: 100,
	},
];

const crossBrowserLinearGradientInColorSpace = [
	{ linearGradientWithBrowserPrefix: 'linear-gradient', colorSpaceSuffix: ' in oklab' },
	{ linearGradientWithBrowserPrefix: '-moz-linear-gradient', colorSpaceSuffix: '' },
];

const UPDATE_TIMEOUT = 750;

export const SatisfactionCell: FC<WidgetProps> = ({ expand, activityId, widget: { satisfaction }, activities, activityName }) => {
	const [value, setValue] = useState(satisfaction);
	const [hover, setHover] = useState(false);
	const intl = useIntl();
	const theme = useTheme();
	const { mutate: updateSatisfaction } = useApiMutation(updateSatisfactionValue);
	const { openSnackbar } = useSnackbar();

	const index = activities.findIndex((activity) => activity?.id === activityId);
	const prev = activities[index - 1]?.satisfaction;
	const next = activities[index + 1]?.satisfaction;

	const createChartBackgroundGradientString = (colorSpace: (typeof crossBrowserLinearGradientInColorSpace)[number]) => {
		const { linearGradientWithBrowserPrefix, colorSpaceSuffix } = colorSpace;
		return `${linearGradientWithBrowserPrefix}(to top${colorSpaceSuffix}, ${
			theme.palette[getNightOrDayPalette(theme)][0]
		} 0%, rgba(255,255,255, 0.6)), ${linearGradientWithBrowserPrefix}(to right${colorSpaceSuffix}, ${determineColor(
			theme,
			prev ?? 50,
		)} 0%, ${determineColor(theme, value)} 40%, ${determineColor(theme, value)} 50%)`;
	};

	useDebounceEffect(
		() => {
			if (value !== satisfaction) {
				updateSatisfaction(
					{ activityId, satisfactionValue: value },
					{
						onSuccess: (result) => {
							mutationResponseHandler({
								result: result.updateActivity.ok,
								toastMessages: {
									success: intl.formatMessage({ id: 'widget.satisfaction.update.success' }),
									error: intl.formatMessage({ id: 'widget.satisfaction.update.error' }),
								},
								errorMessage: result.updateActivity.errorMessage,
								openSnackbar,
							});
						},
					},
				);
			}
		},
		[intl, activityId, satisfaction, updateSatisfaction, value, openSnackbar],
		UPDATE_TIMEOUT,
	);

	return (
		<WidgetCell
			data-activity-widget-name={slugify(activityName, { lower: true })}
			data-activity-widget-type={widgetDefinitions.satisfaction.type}
			expand={expand}
			onMouseEnter={() => setHover(true)}
			onMouseLeave={() => setHover(false)}
		>
			{hover && (
				<Box sx={{ height: 120, py: 1, position: 'absolute', left: 7, top: 16, zIndex: 1 }}>
					<SatisfactionSlider
						sx={{
							'& input[type="range"]': {
								WebkitAppearance: 'slider-vertical',
							},
						}}
						step={null}
						track={false}
						defaultValue={satisfaction}
						orientation={'vertical'}
						onChange={(_, value) => setValue(value as number)}
						value={value}
						marks={MARKS}
					/>
				</Box>
			)}
			<Stack sx={{ height: 120, py: 1, position: 'relative' }} justifyContent={'center'} alignItems={'center'} flexDirection={'row'}>
				<Box
					position={'absolute'}
					height={'100%'}
					left={-25}
					bottom={-16}
					sx={{
						width: 'calc(100% + 50px)',
					}}
				>
					<Chart
						sx={{
							clipPath: `
								polygon(
									50% ${100 - visibleMinimum(value)}%,
									100% ${100 - determineAngle(next ?? 50, value)}%,
									100% 100%,
									0 100%,
									0% ${100 - determineAngle(prev ?? 50, value)}%)
								`,
							WebkitBackfaceVisibility: 'hidden',
							background: crossBrowserLinearGradientInColorSpace.map(createChartBackgroundGradientString),

							backgroundBlendMode: (theme) => (theme.palette.mode === 'dark' ? 'soft-light' : 'lighten'),

							'&:before': {
								content: '""',
								display: 'block',
								position: 'absolute',
								height: '100%',
								opacity: 0.75,
								width: '100%',
								clipPath: `polygon(
									0 ${100 - determineAngle(prev ?? 50, value)}%,
									50% ${100 - visibleMinimum(value)}%,
									100% ${100 - determineAngle(next ?? 50, value)}%,
									100% ${100 - determineAngle(next ?? 50, value) + STROKE_WIDTH}%,
									50% ${100 - visibleMinimum(value) + STROKE_WIDTH}%,
									0 ${100 - determineAngle(prev ?? 50, value) + STROKE_WIDTH}%);`,
								WebkitBackfaceVisibility: 'hidden',
								zIndex: 1,
								// dashed line within chart
								background: (theme) =>
									`linear-gradient(to right, transparent 50%, ${
										theme.palette[getNightOrDayPalette(theme)][0]
									} 50%), linear-gradient(to right, ${determineColor(theme, prev ?? 50)} 0%, ${determineColor(
										theme,
										value,
									)} 40%, ${determineColor(theme, value)} 50%)`,
								backgroundSize: '12px 2%, 100% 2px',
							},
						}}
					/>
				</Box>
				<Box sx={{ height: 80, position: 'relative' }}>
					<Slider
						sx={{
							'& input[type="range"]': {
								WebkitAppearance: 'slider-vertical',
							},
							opacity: 0,
							'.MuiSlider-thumb': {
								cursor: 'ns-resize',
							},
						}}
						step={null}
						track={false}
						defaultValue={50}
						orientation={'vertical'}
						suppressHydrationWarning
						value={value}
						onChange={(_, value) => setValue(value as number)}
						marks={MARKS}
					/>
					<SatisfactionThumb value={value} />
				</Box>
			</Stack>
		</WidgetCell>
	);
};

type Props = Readonly<{ value: number }>;

const Chart = styled('div')`
	position: absolute;
	height: 100%;
	width: 100%;
	left: 0;
	bottom: 0;
	z-index: 0;
	background-color: black;
	pointer-events: none;
`;

const SatisfactionThumb: FC<Props> = ({ value }) => {
	return (
		<Box
			sx={{
				p: 1.5,
				lineHeight: '12px',
				borderRadius: '50%',
				boxShadow: (theme) =>
					theme.palette.mode === 'light'
						? '0px 9px 28px 8px rgba(0, 0, 0, 0.05), 0px 6px 16px 0px rgba(0, 0, 0, 0.08), 0px 3px 6px -4px rgba(0, 0, 0, 0.12)'
						: 'unset',
				position: 'absolute',
				zIndex: 1,
				left: '50%',
				transform: 'translateX(-50%)',
				bottom: `calc(${value}% - 24px)`,
				pointerEvents: 'none',
				outline: (theme) => (theme.palette.mode === 'dark' ? `1px solid ${theme.palette.night[300]}` : 'unset'),
				background: (theme) => theme.palette[getNightOrDayPalette(theme)][0],
			}}
		>
			<SatisfactionRateIcon rate={value} />
		</Box>
	);
};

const determineAngle = (otherValue: number, value: number): number => {
	return visibleMinimum(value / 2 + otherValue / 2);
};

const visibleMinimum = (value: number): number => {
	return value < 20 ? 20 : value;
};
